<template>
  <div class="distro-info">
    <div class="brand-info">
      <div class="b-logo">
        <!-- company.data.company_image in src -->
        <img :src="company.data.company_image
          ? 'https://public-assets.staging.medusadistribution.com/' + company.data.company_image
          : '/images/medusa.png'" alt="Medusa Distribution" />
      </div>
      <div class="info-data">
        <p>{{ company.data.name }} <span>ORDER FORM</span></p>
        <span>Customer since {{ moment(company.data.created_at) }}</span>
      </div>
    </div>
    <div class="header-actions">
      <div class="toggle-group-wrap">
        <SwitchGroup>
          <div class="switch-row">
            <Switch :model-value="inStockOnly" @update:model-value="onToggleInStockOnly" :class="inStockOnly
              ? ' bg-theme-primary'
              : 'bg-gray-200 bg-opacity-20'
              "
              class="relative inline-flex h-4 w-8 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-theme-primary focus:ring-offset-2">
              <span :class="inStockOnly
                ? 'translate-x-4'
                : 'translate-x-1'
                " class="inline-block h-3 w-3 transform rounded-full bg-white transition-transform" />
            </Switch>
            <SwitchLabel class="text-sm text-white font-medium cursor-pointer">In Stock Only</SwitchLabel>
          </div>
        </SwitchGroup>
        <SwitchGroup>
          <div class="switch-row">
            <Switch :model-value="neverOrdered" @update:model-value="onToggleNeverOrdered" :class="neverOrdered
              ? ' bg-theme-primary'
              : 'bg-gray-200 bg-opacity-20'
              "
              class="relative inline-flex h-4 w-8 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-theme-primary focus:ring-offset-2">
              <span :class="neverOrdered
                ? 'translate-x-4'
                : 'translate-x-1'
                " class="inline-block h-3 w-3 transform rounded-full bg-white transition-transform" />
            </Switch>
            <SwitchLabel class="text-sm text-white font-medium cursor-pointer">Never Ordered</SwitchLabel>
          </div>
        </SwitchGroup>
      </div>
      <button type="button" class="collapse-action" @click="allCollapsed ? onExpandAll() : onCollapseAll()"><span class="collapse-label"><span class="collapse-label__sizer">Collapse</span><span class="collapse-label__text">{{ allCollapsed ? "Expand" : "Collapse" }}</span></span>&nbsp;All</button>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, onBeforeUnmount } from "vue";
import { Switch, SwitchGroup, SwitchLabel } from "@headlessui/vue";
import Distribution from "@/modules/Distribution";

export default {
  name: "DistroInfo",
  components: {
    Switch,
    SwitchGroup,
    SwitchLabel,
  },
  props: {
    inStockOnly: {
      type: Boolean,
      default: false,
    },
    neverOrdered: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["update:inStockOnly", "update:neverOrdered"],
  methods: {
    moment: function (date) {
      return moment(date).format("MMMM YYYY");
    },
  },
  setup(props, { emit }) {
    const isCollapsedAll = ref(false);
    const showInStockOnly = ref(false);
    const showNeverOrderedOnly = ref(false);

    const { company, company_details } = Distribution();
    company_details();

    const allCollapsed = ref(false);
    let observer = null;

    const syncCollapsedState = () => {
      const btns = document.querySelectorAll('.disc-btn[data-brand-slug]');
      if (!btns.length) return;
      allCollapsed.value = [...btns].every((b) => b.getAttribute('aria-expanded') === 'false');
    };

    onMounted(() => {
      observer = new MutationObserver(syncCollapsedState);
      observer.observe(document.body, { attributes: true, attributeFilter: ['aria-expanded'], subtree: true });
    });

    onBeforeUnmount(() => observer?.disconnect());

    const onCollapseAll = () => {
      document.querySelectorAll('.disc-btn[aria-expanded="true"]').forEach((b) => b.click());
    };

    const onExpandAll = () => {
      document.querySelectorAll('.disc-btn[aria-expanded="false"]').forEach((b) => b.click());
    };

    const onToggleInStockOnly = () => {
      emit("update:inStockOnly", !props.inStockOnly);
    };

    const onToggleNeverOrdered = () => {
      emit("update:neverOrdered", !props.neverOrdered);
    };

    return {
      onCollapseAll,
      isCollapsedAll,
      allCollapsed,
      onExpandAll,
      onToggleInStockOnly,
      onToggleNeverOrdered,
      company,
      showInStockOnly,
      showNeverOrderedOnly,
    };
  },
};
</script>

<style lang="scss" scoped>
.distro-info {
  @apply w-full flex flex-col gap-4 p-4 rounded-lg text-white justify-between xl:flex-row xl:items-center;
  background: #474557;

  .brand-info {
    @apply flex items-start space-x-3 sm:space-x-4;

    .b-logo {
      @apply h-12 w-12 flex items-center justify-center bg-white rounded-md flex-shrink-0;

      img {
        max-width: calc(100% - 6px);
        max-height: calc(100% - 6px);
      }
    }
  }

  .header-actions {
    @apply flex w-full flex-col items-stretch gap-3 sm:flex-row sm:items-center sm:justify-between xl:w-auto xl:justify-end;
  }

  .toggle-group-wrap {
    @apply flex flex-col items-start gap-2 sm:flex-row sm:flex-wrap sm:items-center sm:gap-4;
  }

  .switch-row {
    @apply inline-flex items-center gap-2 cursor-pointer;
  }


  .info-data {
    @apply flex flex-col;

    p {
      @apply text-base font-normal text-white uppercase;
    }

    span {
      @apply text-sm text-white text-opacity-70;
    }
  }

  button {
    @apply inline-flex items-center text-sm font-normal text-white px-4 py-1.5;

    &:before {
      content: "";
      @apply inline-block w-px h-4 bg-white rounded-full mr-5 opacity-40;
    }
  }

  .collapse-action {
    @apply ml-auto sm:ml-0 xl:ml-auto;

    .collapse-label {
      display: inline-grid;

      &__sizer,
      &__text {
        grid-area: 1 / 1;
      }

      &__sizer {
        visibility: hidden;
      }
    }
  }

  @media (max-width: 639px) {
    button:before {
      @apply hidden;
    }

    .collapse-action {
      @apply self-end px-0;
    }
  }


}
</style>
